import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";
import { ArrowLeft, Clock } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError("Post not found"));
  }, [slug]);

  if (error) {
    return (
      <Section className="pt-28">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-slate-900">Post not found</h1>
          <Link to="/blog" className="mt-4 inline-block text-blue-600 text-sm font-semibold">← Back to blog</Link>
        </div>
      </Section>
    );
  }

  if (!post) {
    return <Section className="pt-28"><div className="text-slate-500">Loading…</div></Section>;
  }

  return (
    <div data-testid="blog-post-page">
      <Section className="pt-28 pb-10">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 font-medium">
          <ArrowLeft className="w-3.5 h-3.5" /> All essays
        </Link>

        <div className="mt-8 max-w-3xl">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
            <span>{post.category}</span>
            <span className="text-slate-300">•</span>
            <span className="inline-flex items-center gap-1 text-slate-500 normal-case tracking-normal font-medium">
              <Clock className="w-3 h-3" /> {post.read_minutes} min read
            </span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-500 normal-case tracking-normal font-medium">{post.published_at}</span>
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
            {post.title}
          </h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>
        </div>

        <div className="mt-10 rounded-2xl overflow-hidden bg-slate-100 aspect-[21/9]">
          <img src={post.cover_image} alt="" className="w-full h-full object-cover" />
        </div>
      </Section>

      <Section className="pt-0">
        <article
          data-testid="blog-post-body"
          className="max-w-3xl text-slate-700 leading-[1.8] text-[17px] [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-slate-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:font-display [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:mb-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-5 [&_ol]:space-y-2 [&_strong]:text-slate-900 [&_strong]:font-semibold [&_a]:text-blue-600 [&_a]:font-medium hover:[&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-blue-500 [&_blockquote]:bg-slate-50 [&_blockquote]:px-5 [&_blockquote]:py-3 [&_blockquote]:rounded-r-lg [&_blockquote]:my-6 [&_blockquote]:text-slate-600 [&_blockquote_p]:mb-0 [&_hr]:my-10 [&_hr]:border-slate-200 [&_table]:w-full [&_table]:my-6 [&_table]:text-[15px] [&_table]:border-collapse [&_th]:text-left [&_th]:font-semibold [&_th]:text-slate-900 [&_th]:bg-slate-50 [&_th]:px-4 [&_th]:py-2.5 [&_th]:border [&_th]:border-slate-200 [&_td]:px-4 [&_td]:py-2.5 [&_td]:border [&_td]:border-slate-200"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </article>

        <div className="mt-14 max-w-3xl rounded-2xl border border-slate-200 bg-slate-50 p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-semibold text-slate-900">Ready to operationalize?</div>
            <p className="text-sm text-slate-600 mt-1">See the platform in action with a 30-minute walkthrough.</p>
          </div>
          <Link
            to="/demo"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Book a Demo
          </Link>
        </div>
      </Section>
    </div>
  );
}
